import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ComparableSales = ({ comparables, isBlurred = false }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'relevanceScore', direction: 'desc' });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount);
  };

  const getConditionColor = (grade) => {
    if (grade >= 4) return 'text-success';
    if (grade >= 3) return 'text-warning';
    return 'text-error';
  };

  const getRelevanceColor = (score) => {
    if (score >= 90) return 'bg-success text-success-foreground';
    if (score >= 75) return 'bg-warning text-warning-foreground';
    return 'bg-muted text-text-secondary';
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedComparables = [...comparables]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const SortIcon = ({ column }) => {
    if (sortConfig?.key !== column) {
      return <Icon name="ArrowUpDown" size={14} className="text-text-secondary" />;
    }
    return sortConfig?.direction === 'asc' ? 
      <Icon name="ArrowUp" size={14} className="text-primary" /> : 
      <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  return (
    <div className="bg-surface border border-border rounded-lg shadow-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-text-primary">Comparable Sales</h2>
          <div className="flex items-center space-x-2">
            <Icon name="Database" size={16} className="text-text-secondary" />
            <span className="text-sm text-text-secondary">{comparables?.length} comparables found</span>
          </div>
        </div>
      </div>
      <div className={`relative ${isBlurred ? 'filter blur-sm' : ''}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left">
                  <button 
                    onClick={() => handleSort('relevanceScore')}
                    className="flex items-center space-x-1 text-sm font-medium text-text-primary hover:text-primary transition-smooth"
                  >
                    <span>Relevance</span>
                    <SortIcon column="relevanceScore" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button 
                    onClick={() => handleSort('make')}
                    className="flex items-center space-x-1 text-sm font-medium text-text-primary hover:text-primary transition-smooth"
                  >
                    <span>Make & Model</span>
                    <SortIcon column="make" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button 
                    onClick={() => handleSort('year')}
                    className="flex items-center space-x-1 text-sm font-medium text-text-primary hover:text-primary transition-smooth"
                  >
                    <span>Year</span>
                    <SortIcon column="year" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button 
                    onClick={() => handleSort('hours')}
                    className="flex items-center space-x-1 text-sm font-medium text-text-primary hover:text-primary transition-smooth"
                  >
                    <span>Hours</span>
                    <SortIcon column="hours" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button 
                    onClick={() => handleSort('condition')}
                    className="flex items-center space-x-1 text-sm font-medium text-text-primary hover:text-primary transition-smooth"
                  >
                    <span>Condition</span>
                    <SortIcon column="condition" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <button 
                    onClick={() => handleSort('askingPrice')}
                    className="flex items-center space-x-1 text-sm font-medium text-text-primary hover:text-primary transition-smooth"
                  >
                    <span>Asking Price</span>
                    <SortIcon column="askingPrice" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left">Location</th>
                <th className="px-4 py-3 text-left">Dealer</th>
                <th className="px-4 py-3 text-left">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sortedComparables?.map((comp, index) => (
                <tr key={index} className="hover:bg-muted/50 transition-smooth">
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRelevanceColor(comp?.relevanceScore)}`}>
                      {comp?.relevanceScore}%
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-medium text-text-primary">{comp?.make} {comp?.model}</div>
                    <div className="text-sm text-text-secondary">{comp?.capacity} tons</div>
                  </td>
                  <td className="px-4 py-4 text-text-primary">{comp?.year}</td>
                  <td className="px-4 py-4 text-text-primary">{comp?.hours?.toLocaleString()}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-2">
                      <span className={`font-medium ${getConditionColor(comp?.condition)}`}>
                        {comp?.condition}/5
                      </span>
                      <div className="flex space-x-1">
                        {[1, 2, 3, 4, 5]?.map((star) => (
                          <Icon
                            key={star}
                            name="Star"
                            size={12}
                            className={star <= comp?.condition ? 'text-warning fill-current' : 'text-muted'}
                          />
                        ))}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-semibold text-text-primary">{formatCurrency(comp?.askingPrice)}</div>
                    <div className="text-sm text-text-secondary">Listed {comp?.daysListed} days ago</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-text-primary">{comp?.location?.city}, {comp?.location?.state}</div>
                    <div className="text-sm text-text-secondary">{comp?.location?.distance} miles away</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-text-primary">{comp?.dealer?.name}</div>
                    <div className="text-sm text-text-secondary">{comp?.dealer?.phone}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={comp?.source === 'auction' ? 'Gavel' : 'Store'} 
                        size={14} 
                        className="text-text-secondary" 
                      />
                      <span className="text-sm text-text-secondary capitalize">{comp?.source}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isBlurred && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80">
            <div className="text-center p-6">
              <Icon name="Lock" size={48} className="text-text-secondary mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-text-primary mb-2">Premium Feature</h3>
              <p className="text-text-secondary mb-4">Upgrade to view detailed comparable sales data</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparableSales;